"use client";

import React, { useState, useEffect, forwardRef, useMemo, useCallback, useRef } from 'react';

import ShiftAssigmentService from '../services/shift-assignment-service';
import { ShiftCalendar } from '../types/shift-assignment.types';
import { isSuccess } from '../../../models/response/base-response';
import DepartmentService from '../../department/services/department-service';
import UserService from '../../user/services/user-service';
import { showToast } from '../../../components/custom/custom-toast';
import { SelectOneDropdown } from '../../../components/custom/select-one-dropdown';
import { ATTENDANCE_MODE } from '../../../constants/status';
import DateInputCustom from '../../../components/custom/date-input/date-input-custom';
import { useStoreOptions } from '../../../hooks/use-store-options';

// DateInputCustom trả chuỗi dd/MM/yyyy — parse để so sánh trước/sau (không dùng `new Date(str)` vì
// engine JS không đảm bảo hiểu đúng format dd/MM/yyyy, dễ nhầm sang MM/dd/yyyy).
const parseDMY = (dmy: string): Date | null => {
    if (!dmy) return null;
    const [d, m, y] = dmy.split('/').map(Number);
    if (!d || !m || !y) return null;
    return new Date(y, m - 1, d);
};

interface Department { id: string; name: string; status: number;}
interface Employee { id: string; name: string; departmentId: string; attendanceMode?: ATTENDANCE_MODE | null; }

export interface ShiftAssignmentFormData {
    shiftId: string;
    start_date: string;
    end_date: string;
    note: string;
    employee_ids: number[];
    department_id: number | undefined;
    branch_id?: number;
    mode: 'create' | 'edit' | 'batch';
}

interface ShiftAssignmentCreateModalProps {
  onClose: () => void;
  onSubmit: (data: ShiftAssignmentFormData) => void;
  onSuccess: () => void;
  
  initialValues: {
    shiftId?: string;
    start_date?: string;
    end_date?: string;
    note?: string;
    employeeId?: string;
    date?: Date;
  };
  
  mode: 'create' | 'edit' | 'batch';

  shifts: ShiftCalendar[];
  isLoadingShifts: boolean;

  /**
   * Chi nhánh đang chọn trên trang "gán ca" — dùng làm giá trị khởi tạo. Ở mode `batch`, modal có
   * dropdown chi nhánh RIÊNG (xem `selectedBranchId` bên dưới) để gán ca cho chi nhánh khác mà không
   * cần thoát ra đổi filter ngoài trang trước.
   */
  branchId?: number;
}

const ShiftAssignmentCreateModal = forwardRef<HTMLDivElement, ShiftAssignmentCreateModalProps>((
  { onSuccess, onClose, onSubmit, initialValues, mode, shifts, isLoadingShifts, branchId },
  ref
) => {
    const [formData, setFormData] = useState({
        assignmentType: 'employee',
        shift: initialValues?.shiftId || '', 
        startTime: initialValues?.start_date || '', 
        endTime: initialValues?.end_date || '',
        departmentId: '', 
        notes: initialValues?.note || '',
    });
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
    const [errors, setErrors] = useState<any>({});

    // Chi nhánh dùng để lọc roster + gắn vào phân công — mặc định lấy chi nhánh đang chọn ngoài
    // trang, nhưng ở mode `batch` cho phép đổi ngay trong modal (xem UI "Chi nhánh" bên dưới).
    const { options: branchOptions, loading: isLoadingBranches } = useStoreOptions();
    const [selectedBranchId, setSelectedBranchId] = useState<number | undefined>(branchId);
    useEffect(() => {
        if (!selectedBranchId && branchOptions.length > 0) {
            setSelectedBranchId(branchOptions[0].value);
        }
    }, [branchOptions, selectedBranchId]);

    const [departments, setDepartments] = useState<Department[]>([]);
    const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
    
    const { fetch: fetchCreateAssignment } = ShiftAssigmentService.createShiftAssignments();
    const { fetch: fetchDepartmentList, loading: loadingListDeparment } = DepartmentService.list();
    const { fetch: fetchListUsers, loading: loadingListUsers } = UserService.list();

    const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);

    const isBatchMode = mode === 'batch';
    const title = isBatchMode ? "GÁN CA HÀNG LOẠT" : "PHÂN CÔNG LÀM VIỆC";

    const loadDepartments = useCallback(async () => {
        const res = await fetchDepartmentList({ status: -1 })

        if (res && isSuccess(res) && Array.isArray(res.data)) {
            // Màn gán ca chỉ dành cho NV làm theo ca — bộ phận có mặc định chấm công khác SHIFT
            // (giờ hành chính/không chấm công) không nên hiện ra để chọn ở đây, đồng bộ với BE
            // (filterShiftModeEmployeeIds ở hrm/shift.service.ts).
            setDepartments(
                res.data
                    .filter((d: any) => (d.default_attendance_mode || ATTENDANCE_MODE.SHIFT) === ATTENDANCE_MODE.SHIFT)
                    .map((d: any) => ({
                        id: String(d.id),
                        name: d.name || d.department_name,
                        status: d.status || 0,
                    })),
            );
        } else {
            console.error("Lỗi: Dữ liệu danh sách bộ phận không hợp lệ.");
            setDepartments([]);
        }
    }, []);
    
    const loadEmployees = useCallback(async (departmentId: string | null) => {
        if (!departmentId) {
            setAllEmployees([]);
            return;
        }

        const res = await fetchListUsers({
            status: 1,
            department_id: Number(departmentId),
            ...(selectedBranchId ? { branch_id: selectedBranchId } : {}),
        });

        if (res && isSuccess(res) && Array.isArray(res.data.list)) {
            // Chỉ gán ca cho NV loại "làm theo ca" — NV giờ hành chính/không chấm công có lịch riêng
            // (work_schedule_templates), không xếp ca ở đây. attendance_mode do BE trả về đã là giá
            // trị resolve sẵn (ghi đè cá nhân ?? mặc định bộ phận), thiếu field thì coi như SHIFT để
            // tương thích ngược với dữ liệu cũ — khớp quy ước bên hrm (filterShiftModeUsers).
            setAllEmployees(
                res.data.list
                    .filter((d: any) => (d.attendance_mode || ATTENDANCE_MODE.SHIFT) === ATTENDANCE_MODE.SHIFT)
                    .map((d: any) => ({
                        id: String(d.id),
                        name: d.full_name || d.name,
                        departmentId: String(d.department_id),
                        attendanceMode: d.attendance_mode,
                    })),
            );
        } else {
            console.error("Lỗi: Dữ liệu danh sách nhân viên không hợp lệ.");
            setAllEmployees([]);
        }
    }, [selectedBranchId]);

    useEffect(() => {
        if (formData.assignmentType === 'employee') {
            void loadEmployees(formData.departmentId);
        } else {
            setAllEmployees([]);
        }
    }, [formData.departmentId, formData.assignmentType, loadEmployees]);

    useEffect(() => {
        void loadDepartments();

        if (initialValues) {
            // ... (Logic khởi tạo giữ nguyên)
        }
    }, [initialValues, loadDepartments]);

    const filteredEmployees = useMemo(() => {
        return allEmployees; 
    }, [allEmployees]); 


    const validate = useCallback(() => {
        const newErrors: any = {};
        let isValid = true;
        
        if (!formData.shift) { newErrors.shift = 'Vui lòng chọn ca làm việc.'; isValid = false; }
        if (!formData.startTime) { newErrors.startTime = 'Vui lòng chọn thời gian bắt đầu.'; isValid = false; }
        if (!formData.endTime) { newErrors.endTime = 'Vui lòng chọn thời gian kết thúc.'; isValid = false; }

        if (formData.startTime && formData.endTime) {
            const start = parseDMY(formData.startTime);
            const end = parseDMY(formData.endTime);
            if (start && end && start > end) {
                newErrors.endTime = 'Thời gian kết thúc phải sau hoặc bằng thời gian bắt đầu.';
                isValid = false;
            }
        }

        // Không bắt buộc — BE (CreateShiftAssignmentDto.note) coi đây là ghi chú tự do hoàn toàn
        // optional, và luồng "gán ca nhanh" (bấm + trên ô lịch) tự điền sẵn "Gán ca nhanh" mà không
        // hỏi user, nên bắt nhập ở đây là không nhất quán.

        if (formData.assignmentType === 'employee') {
            if (!formData.departmentId || selectedEmployeeIds.length === 0) { // Kiểm tra cả departmentId và employeeId
                 newErrors.employee = 'Vui lòng chọn ít nhất một nhân viên.';
                 isValid = false;
            }
        } else if (formData.assignmentType === 'department') {
            if (!formData.departmentId) {
                newErrors.departmentId = 'Vui lòng chọn bộ phận.';
                isValid = false;
            }
        }
        
        setErrors(newErrors);
        return isValid;
    }, [formData, selectedEmployeeIds]);

        const handleSubmit = useCallback(async (e: React.FormEvent) => {

            e.preventDefault();

            

            if (!validate()) {

                return;

            }

    

            let employeeIdsToSend: number[] = [];

    

            if (formData.assignmentType === 'employee') {

                employeeIdsToSend = selectedEmployeeIds.map(id => Number(id));

            } else if (formData.assignmentType === 'department') {

                employeeIdsToSend = [-1]; 

            }

    

            const dataToSend: ShiftAssignmentFormData = {

                shiftId: formData.shift,

                // DateInputCustom (type="date") đã trả sẵn chuỗi dd/MM/yyyy — không cần convert qua ISO nữa.
                start_date: formData.startTime,

                end_date: formData.endTime,

                note: formData.notes,

                

                employee_ids: employeeIdsToSend,

                department_id: formData.departmentId ? Number(formData.departmentId) : undefined,

                branch_id: selectedBranchId,

                mode: mode,

            };

            

            setIsSubmittingLocal(true);

    

            try {

                const res = await fetchCreateAssignment(

                    dataToSend.shiftId,

                    dataToSend

                );

    

                if (res && isSuccess(res)) {

                    showToast({ 

                        message: "Phân công ca thành công!", 

                        type: "success" 

                    });

                    console.log("Phân công ca thành công:", res); 

                    onSuccess();

                    onClose(); 

                } else {

                    const errorMessage = res?.message || "Đã xảy ra lỗi khi tạo phân công. Vui lòng thử lại.";

                    showToast({ 

                        message: errorMessage, 

                        type: "error" 

                    });

                    console.error("Lỗi phân công ca:", errorMessage);

                }

            } catch (error) {

                showToast({ 

                    message: "Lỗi kết nối mạng hoặc hệ thống. Không thể gửi yêu cầu.", 

                    type: "error" 

                });

                console.error("Lỗi network khi phân công ca:", error);

            } finally {

                setIsSubmittingLocal(false);

            }

        }, [validate, formData, selectedEmployeeIds, fetchCreateAssignment, onSuccess, onClose, mode, selectedBranchId]);

    const getInputClassName = useCallback((field: keyof typeof formData | 'employee' | 'departmentId') => 
        `w-full p-2 border rounded-md focus:ring-green-500 focus:border-green-500 ${
            errors[field] ? 'border-red-500' : 'border-gray-300'
        }`, [formData, errors]);
        
    const handleDateChange = (field: 'startTime' | 'endTime', dateString: string) => {
        setFormData(prev => ({ ...prev, [field]: dateString }));
        if (errors[field]) {
            setErrors((prev: any) => ({ ...prev, [field]: null }));
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (name === 'departmentId') {
            // Reset Nhân viên khi đổi bộ phận (vì danh sách mới đang được tải)
            setSelectedEmployeeIds([]); 
        }
        
        if (name === 'assignmentType' && value === 'employee') {
            // Giữ nguyên departmentId nếu đã chọn, nếu không, sẽ tải lại nhân viên (nếu departmentId có giá trị)
        }

        if (errors[name]) {
            setErrors((prev: any) => ({ ...prev, [name]: null }));
        }
    };

    const handleEmployeeToggle = (employeeId: string) => {
        setSelectedEmployeeIds(prev => {
            setErrors((prev: any) => ({ ...prev, employee: null }));
            if (prev.includes(employeeId)) {
                return prev.filter(id => id !== employeeId);
            } else {
                return [...prev, employeeId];
            }
        });
    };

    const getShiftLabel = useCallback((shift: ShiftCalendar) => {
        if (shift.code === 'OFF' || shift.start_time === '00:00') {
            return shift.name;
        }
        return `${shift.name} (${shift.start_time}-${shift.end_time})`;
    }, []);

  return (
    <div className="fixed inset-0 z-[90] bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div ref={ref} className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto transform transition-all duration-300">
            {/* Header */}
            <div className="p-5 border-b flex justify-between items-center bg-gray-50">
                <h2 className="text-xl font-bold text-green-700">{title}</h2> 
                <button 
                    onClick={onClose} 
                    className="text-gray-500 hover:text-red-500 transition"
                    aria-label="Đóng"
                >
                    <span className="text-2xl">✕</span>
                </button>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {isBatchMode && (
                    <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700">
                            Chi nhánh <span className="text-red-500">*</span>
                        </label>
                        {/* Chọn riêng trong modal — không phụ thuộc chi nhánh đang lọc ngoài trang, cho
                        phép gán ca cho chi nhánh khác mà không cần thoát ra đổi filter trước. */}
                        <select
                            className="w-full p-2 border rounded-md border-gray-300 focus:ring-green-500 focus:border-green-500"
                            value={selectedBranchId ?? ''}
                            onChange={(e) => {
                                setSelectedBranchId(e.target.value ? Number(e.target.value) : undefined);
                                setFormData(prev => ({ ...prev, departmentId: '' }));
                                setSelectedEmployeeIds([]);
                            }}
                            disabled={isLoadingBranches}
                        >
                            {branchOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <label className="block text-sm font-semibold text-gray-700">
                            Loại phân công <span className="text-red-500">*</span>
                        </label>
                        <div className="space-y-2">
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input 
                                    type="radio" 
                                    name="assignmentType" 
                                    value="employee"
                                    checked={formData.assignmentType === 'employee'}
                                    onChange={handleChange}
                                    className="text-green-600 focus:ring-green-500" 
                                />
                                <span>Phân công nhân viên</span>
                            </label>
                            {/* Tạm ẩn "Phân công bộ phận" — chỉ dùng "Phân công nhân viên" ở giai đoạn này theo yêu cầu.
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="assignmentType"
                                    value="department"
                                    checked={formData.assignmentType === 'department'}
                                    onChange={handleChange}
                                    className="text-green-600 focus:ring-green-500"
                                />
                                <span>Phân công bộ phận</span>
                            </label>
                            */}
                        </div>
                    </div>
                    
                    <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700">
                            Ca làm việc <span className="text-red-500">*</span>
                            {isLoadingShifts && <span className="ml-2 text-xs text-blue-500">(Đang tải...)</span>}
                        </label>
                        <select
                            name="shift"
                            className={getInputClassName('shift')}
                            value={formData.shift}
                            onChange={handleChange}
                            disabled={isLoadingShifts}
                        >
                            <option value="">Chọn ca làm việc</option>
                            
                            {shifts.map(shift => (
                                <option key={shift.id} value={shift.id}>
                                    {getShiftLabel(shift)}
                                </option>
                            ))}
                            
                        </select>
                        {errors.shift && <p className="text-red-500 text-xs mt-1">{errors.shift}</p>}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                        Thời gian bắt đầu <span className="text-red-500">*</span>
                    </label>
                    <DateInputCustom
                        type="date"
                        value={formData.startTime}
                        maxDate={formData.endTime || undefined}
                        onChange={(d) => handleDateChange('startTime', d ?? '')}
                        className="w-full"
                    />
                    {errors.startTime && <p className="text-red-500 text-xs mt-1">{errors.startTime}</p>}
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                        Thời gian kết thúc <span className="text-red-500">*</span>
                    </label>
                    <DateInputCustom
                        type="date"
                        value={formData.endTime}
                        minDate={formData.startTime || undefined}
                        onChange={(d) => handleDateChange('endTime', d ?? '')}
                        className="w-full"
                    />
                    {errors.endTime && <p className="text-red-500 text-xs mt-1">{errors.endTime}</p>}
                </div>
            </div>
                {(formData.assignmentType === 'department' || formData.assignmentType === 'employee') && (
                    <div className="space-y-6">
                        {/* 1. Dropdown Bộ phận */}
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">
                                Bộ phận {formData.assignmentType === 'department' && <span className="text-red-500">*</span>}
                            </label>
                            <SelectOneDropdown<Department>
                                disabled={loadingListDeparment}
                                dataOptions={departments}
                                getOptionLabel={(option) => option.name}
                                getOptionKey={(option) => option.id}
                                selectedId={formData.departmentId}
                                onChange={(selected) => {
                                    const departmentId = selected ? selected.id : '';
                                    setFormData(prev => ({ ...prev, departmentId: departmentId }));
                                    setSelectedEmployeeIds([]); 
                                    
                                    if (errors.departmentId) {
                                        setErrors((prev: any) => ({ ...prev, departmentId: null }));
                                    }
                                }}
                                placeholder="Chọn bộ phận"
                                className={errors.departmentId ? 'border-red-500' : 'border-gray-300'}
                            />
                            {errors.departmentId && <p className="text-red-500 text-xs mt-1">{errors.departmentId}</p>}
                        </div>

                        {/* 2. Danh sách Nhân viên */}
                        {formData.assignmentType === 'employee' && (
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Nhân viên <span className="text-red-500">*</span>
                                </label>
                                
                                {formData.departmentId ? (
                                    <div 
                                        className={`grid grid-cols-3 gap-x-6 gap-y-2 p-4 border rounded-md max-h-48 overflow-y-auto ${getInputClassName('employee')}`}
                                        style={{ height: 'auto' }}
                                    >
                                        {filteredEmployees.length === 0 ? (
                                            <p className="text-gray-500 col-span-3">Không có nhân viên nào trong bộ phận này.</p>
                                        ) : (
                                            filteredEmployees.map(emp => (
                                                <label key={emp.id} className="flex items-center space-x-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedEmployeeIds.includes(emp.id)}
                                                        onChange={() => handleEmployeeToggle(emp.id)}
                                                        className="text-green-600 focus:ring-green-500 rounded"
                                                    />
                                                    <span>{emp.name}</span>
                                                </label>
                                            ))
                                        )}
                                    </div>
                                ) : (
                                    <div 
                                        className="p-4 border border-gray-300 rounded-md text-gray-500 text-sm bg-gray-50"
                                    >
                                        Vui lòng chọn **Bộ phận** để tải danh sách nhân viên.
                                    </div>
                                )}
                                {errors.employee && <p className="text-red-500 text-xs mt-1">{errors.employee}</p>}
                            </div>
                        )}
                    </div>
                )}
                
                {/* Phần 4: Ghi chú */}
                <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                        Ghi chú
                    </label>
                    <textarea
                        rows={3}
                        name="notes"
                        placeholder="Nhập ghi chú phân công ca làm việc (không bắt buộc)"
                        className={getInputClassName('notes')}
                        value={formData.notes}
                        onChange={handleChange}
                    ></textarea>
                    {errors.notes && <p className="text-red-500 text-xs mt-1">{errors.notes}</p>}
                </div>

                {/* Footer / Action Buttons */}
                <div className="flex justify-end pt-4 border-t">
                    <button 
                        type="submit" 
                        disabled={isSubmittingLocal}
                        className="px-6 py-2 bg-green-600 text-white font-semibold rounded-md 
                                   hover:bg-green-700 transition disabled:bg-gray-400"
                    >
                        {isSubmittingLocal ? 'Đang xử lý...' : 'PHÂN CÔNG'}
                    </button>
                </div>

            </form>
        </div>
    </div>
  );
});

export default React.memo(ShiftAssignmentCreateModal);